import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable, type Column } from '../components/DataTable';
import { Icon } from '../components/icons';
import { Badge, EmptyState, ErrorState, LoadingRows, PageHeader } from '../components/ui';
import { errorMessage } from '../lib/errors';
import { formatPhone, relativeTime } from '../lib/format';
import { useContacts } from '../lib/queries';
import type { Contact } from '../lib/types';

/** Kişi listesi — GET /api/contacts (arama + konuşma özeti). Kişi 360'a giriş. */
export default function ContactsPage() {
  const [search, setSearch] = useState('');
  const contacts = useContacts(search);
  const navigate = useNavigate();

  const rows = contacts.data?.items ?? [];

  const columns: Column<Contact>[] = [
    {
      key: 'name',
      header: 'Kişi',
      render: (row) => (
        <span className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-surface-2 text-[10px] font-semibold text-ink-2">
            {(row.name || '?')
              .split(' ')
              .map((part) => part[0])
              .slice(0, 2)
              .join('')}
          </span>
          <span className="max-w-[240px] truncate">{row.name || 'Kayıtlı değil'}</span>
          {row.blocked ? <Badge tone="danger">engelli</Badge> : null}
        </span>
      )
    },
    { key: 'phone', header: 'Telefon', width: '180px', render: (row) => <span className="mono text-[12px]">{formatPhone(row.phone)}</span> },
    {
      key: 'optin',
      header: 'Opt-in',
      width: '100px',
      render: (row) => (row.optIn ? <Badge tone="ok">var</Badge> : <Badge tone="danger">yok</Badge>)
    },
    { key: 'labels', header: 'Etiketler', width: '180px', render: (row) => <span className="text-[12px] text-muted">{row.labels.join(', ') || '—'}</span> },
    { key: 'orders', header: 'Sipariş', width: '80px', align: 'right', render: (row) => <span className="mono text-[12px]">{row.orders.length}</span> },
    { key: 'tickets', header: 'Ticket', width: '80px', align: 'right', render: (row) => <span className="mono text-[12px]">{row.ticketCount}</span> },
    { key: 'seen', header: 'Son görülme', width: '130px', render: (row) => <span className="mono text-[12px] text-muted">{relativeTime(row.lastSeenAt)}</span> }
  ];

  return (
    <div>
      <PageHeader title="Kişiler" subtitle="WhatsApp numaraları, opt-in durumu ve sipariş geçmişi." />
      <div className="flex items-center gap-2 border-b border-line bg-surface px-6 py-2">
        <div className="relative w-[320px]">
          <Icon name="search" size={14} className="pointer-events-none absolute left-2 top-2 text-muted" />
          <input
            className="input pl-7"
            placeholder="İsim, numara veya etiket ara"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Kişi ara"
          />
        </div>
        <span className="ml-auto text-[11px] text-muted">{contacts.data?.items.length ?? 0} kişi</span>
      </div>

      {contacts.isError ? (
        <ErrorState message={errorMessage(contacts.error)} onRetry={() => void contacts.refetch()} />
      ) : contacts.isLoading ? (
        <LoadingRows rows={8} columns={5} />
      ) : rows.length === 0 ? (
        <EmptyState icon="users" title="Kişi bulunamadı" hint="Arama ölçütlerini değiştirin." />
      ) : (
        <DataTable
          caption="Kişi listesi"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate('/kisi/' + row.id)}
          emptyTitle="Kişi bulunamadı"
          emptyHint="Arama ölçütlerini değiştirin."
        />
      )}
    </div>
  );
}
